import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Calendar, User } from 'lucide-react';
import axios from 'axios';
import logger from '@/utils/logger';
import Seo from '@/components/Seo';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BlogPage() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState('all');

  const categories = [
    { id: 'all', label: 'All Posts' },
    { id: 'history', label: 'History' },
    { id: 'gift-guide', label: 'Gift Guides' },
    { id: 'how-to', label: 'How To' },
    { id: 'behind-the-scenes', label: 'Behind the Scenes' }
  ];

  useEffect(() => {
    const fetchPosts = async () => {
      try {
        setLoading(true);
        const url = activeCategory === 'all' 
          ? `${API}/blog` 
          : `${API}/blog?category=${activeCategory}`;
        const response = await axios.get(url);
        setPosts(response.data);
      } catch (error) {
        logger.error('Error fetching blog posts:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchPosts();
  }, [activeCategory]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  return (
    <div className="pt-28 pb-16" data-testid="blog-page">
      <Seo title="Journal" description={"Stories from Lindy's studio — the meaning behind each bracelet, the morning ritual of stringing beads, and quiet thoughts from Paphos."} path="/blog" />
      <div className="container-custom px-6 md:px-12 lg:px-24">
        {/* Header */}
        <div className="text-center mb-12">
          <p className="text-caption text-[#84A98C] mb-4">Our Journal</p>
          <h1 className="heading-display text-4xl md:text-5xl text-[#354F52] mb-4">
            The Whispering Wristwear Blog
          </h1>
          <p className="text-[#52796F] max-w-lg mx-auto">
            Stories, guides, and inspiration for meaningful gifts and 
            the art of secret messages.
          </p>
        </div>

        {/* Category Filter */}
        <div className="flex flex-wrap justify-center gap-2 mb-12">
          {categories.map((cat) => (
            <button
              key={cat.id}
              onClick={() => setActiveCategory(cat.id)}
              className={`px-4 py-2 rounded-full text-sm uppercase tracking-wider transition-all ${
                activeCategory === cat.id
                  ? 'bg-[#354F52] text-[#FAF9F6]'
                  : 'bg-[#F5F5F0] text-[#354F52] hover:bg-[#354F52]/10'
              }`}
              data-testid={`blog-filter-${cat.id}`}
            >
              {cat.label}
            </button>
          ))}
        </div>

        {/* Blog Posts Grid */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="animate-pulse">
                <div className="aspect-[16/10] bg-[#CAD2C5] rounded-sm mb-4" />
                <div className="h-4 bg-[#CAD2C5] rounded w-1/4 mb-3" />
                <div className="h-6 bg-[#CAD2C5] rounded w-3/4 mb-3" />
                <div className="h-4 bg-[#CAD2C5] rounded w-full" />
              </div>
            ))}
          </div>
        ) : posts.length === 0 ? (
          <div className="text-center py-16">
            <p className="text-[#84A98C]">No blog posts found in this category.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {posts.map((post, index) => (
              <article 
                key={post.id} 
                className={`group ${index === 0 && posts.length > 2 ? 'md:col-span-2 lg:col-span-2' : ''}`}
                data-testid={`blog-post-card-${post.slug}`}
              >
                <Link to={`/blog/${post.slug}`}>
                  {/* Image */}
                  <div className="relative overflow-hidden rounded-sm mb-4">
                    <img
                      src={post.image}
                      alt={post.title}
                      className={`w-full object-cover transition-transform duration-700 group-hover:scale-105 ${
                        index === 0 && posts.length > 2 ? 'aspect-[16/9]' : 'aspect-[16/10]'
                      }`}
                    />
                    <div className="absolute top-4 left-4">
                      <span className="text-xs uppercase tracking-widest bg-[#FAF9F6] text-[#354F52] px-3 py-1.5 rounded-full">
                        {post.category.replace('-', ' ')}
                      </span>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="space-y-3">
                    {/* Meta */}
                    <div className="flex items-center gap-4 text-sm text-[#84A98C]">
                      <span className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" strokeWidth={1.5} />
                        {formatDate(post.created_at)}
                      </span>
                      <span className="flex items-center gap-1">
                        <User className="w-4 h-4" strokeWidth={1.5} />
                        {post.author}
                      </span>
                    </div>

                    {/* Title */}
                    <h2 className={`font-serif text-[#354F52] group-hover:text-[#84A98C] transition-colors ${
                      index === 0 && posts.length > 2 ? 'text-2xl md:text-3xl' : 'text-xl'
                    }`}>
                      {post.title}
                    </h2>

                    {/* Excerpt */}
                    <p className="text-[#52796F] leading-relaxed line-clamp-2">
                      {post.excerpt}
                    </p>

                    {/* Read More */}
                    <span className="inline-flex items-center gap-2 text-sm text-[#D4AF37] font-medium group-hover:gap-3 transition-all">
                      Read More
                      <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
                    </span>
                  </div>
                </Link>
              </article>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
